import {
  requestCreator,
  RequestCreator,
  PaymentRequestCreator,
  makeUrl,
  CONFIGURABLE_API_ENDPOINT,
  CONFIGURABLE_PAY_API_ENDPOINT,
} from '@/lib/apiHelpers';
import { call, put } from 'redux-saga/effects';
import { signInAction, signOutAction, expiredAction } from '@/domain/env';
import tokenKeeper from '@/lib/apiTokenKeeper';

const SAGA_SYNC_ACTION_FAILURE = 'SAGA/SYNC_ACTION/FAILURE';

export function logoutAction(action, err) {
  // @to-do catch network error by its type until it reaches
  // this line and be sure err.response is present

  if (
    action.type !== signInAction.type &&
    err.response &&
    err.response.status &&
    err.response.status.toString() === process.env.REACT_APP_UNAUTHORIZED_CODE
  ) {
    return {
      type: signOutAction.type,
    };
  }

  if (action.type === signInAction.type && err.response && !err.response.status) {
    const fakeError = { response: { data: 'Something went wrong. Please, refresh the page or contact support' } };

    return {
      type: action.failure,
      err: fakeError,
    };
  }

  if (
    err.response &&
    err.response.status &&
    err.response.status.toString() === process.env.REACT_APP_TRIAL_EXPIRED_CODE
  ) {
    return {
      type: expiredAction.type,
    };
  }

  return {
    type: action.failure || SAGA_SYNC_ACTION_FAILURE,
    err,
  };
}

export function* doLogout(action, err) {
  yield put(logoutAction(action, err));
}

export function ensure(api, action, { adapter = (d) => d, onSuccess, onFailure } = {}) {
  return function* requestApi(args = {}) {
    yield put({ type: action.request });
    try {
      const resp = yield call(api, args);
      yield put({
        ...args,
        type: action.success,
        payload: adapter(resp),
      });
      if (typeof onSuccess === 'function') onSuccess(resp);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('error in ensure', err);
      yield doLogout(action, err);
      if (typeof onFailure === 'function') onFailure(err);
    }
  };
}

const API_ROOT_FULL = `${CONFIGURABLE_API_ENDPOINT || ''}/v2/3/`;
// The PAY service sits at `/api/pay/`, outside the AP `/v2/{client}/` prefix.
// Mirrored in @/initializers/paymentApiClientInitializer for the typed client.
const PAY_API_ROOT_FULL = `${CONFIGURABLE_PAY_API_ENDPOINT || ''}/pay/`;

const publickRequest = requestCreator(API_ROOT_FULL);
const request = requestCreator(API_ROOT_FULL, tokenKeeper);
const uploadRequest = requestCreator(API_ROOT_FULL, tokenKeeper);

const rcInstance = new RequestCreator(API_ROOT_FULL, tokenKeeper);
const rcPaymentInstance = new PaymentRequestCreator(PAY_API_ROOT_FULL, tokenKeeper);

export default {
  getDevices: rcInstance.get(makeUrl`getSigners/${'id'}`),
  connectHashDevice: rcInstance.post('hash/authorization'),
  saveHashCompanyId: request.post('hash/save_company_id'),
  signIn: publickRequest.post('loginUser'),
  checkTwoFACode: publickRequest.post('checkTwoFACode'),
  resendTwoFACode: publickRequest.post('resendTwoFACode'),
  signInSage: request.post('sage_sa/authorization'),
  companyLogin: request.get('companyLogin'),
  saveSageCompany: request.post('sage_sa/save_company_id'),
  signInPriority: request.post('priority_api/authorization'),
  signInSAP: request.post('sap_api/authorization'),
  signInNetSuite: request.post('netsuite_api/authorization'),
  signInMDNavision: request.post('microsoft_dynamics_nav/authorization'),
  saveMDNavisionCompany: request.post('microsoft_dynamics_nav/save_company_id'),
  signInMDBC: request.post('microsoft_dynamics_bc/authorization'),
  saveMDBCCompany: request.post('microsoft_dynamics_bc/save_company_id'),
  signInAcumatica: request.post('acumatica_api/authorization'),
  signInAcumaticaAdvanced: request.post('acumatica_advanced_api/authorization'),
  signInHashWeb: request.post('hash_web/authorization'),
  // signInNetSuit: rcInstance.mock({}),
  savePriorityCompany: request.post('priority_api/save_company_id'),
  signInSAP4Hana: request.post('sap4hana_api/authorization'),
  saveSAP4Hana: request.post('sap4hana_api/save_company_id'),
  getCompanies: rcInstance.get('getListOfCompanies'),
  getCompaniesGrid: rcInstance.get('grid/companies'),
  markCompanyPinned: request.get('getListOfCompanies'), // @todo fix url
  getListRecent: request.get('listRecent'),
  getDocumentsExportFormats: request.get('exportFormats'),
  documentSearch: rcInstance.get('search'),
  documentSearchByCategory: rcInstance.get('search_new'),
  documentSearchBySupplier: rcInstance.get('supplier_view/search'),
  searchInCompanies: rcInstance.get('search_in_companies'),
  getDocument: rcInstance.get('getDocumentMetadata'),
  getOptionsList: rcInstance.get('getOptionsList'),
  removeDocument: request.post('removeDocument'),
  updateDocumentTags: rcInstance.post('updateTags'),
  updateMultipleDocumentsTags: rcInstance.post('updateTagsDocuments/'),
  changeTypeDocuments: rcInstance.post('changeTypeDocuments'),
  changeTypeDocument: rcInstance.post('changeTypeDocument'),
  linkDocument: request.post('linkDocument'),
  linkDocuments: request.post('linkDocuments'),
  lockLinkedDocuments: request.post('updateLinkState'),
  unlinkDocument: request.post('unlinkDocument'),
  unlinkMultiDocuments: request.post('unlinkMultiDocuments'),
  updateLink: request.post('updateLink'),
  updateNotes: rcInstance.post('updateNotes'),
  removeNotes: rcInstance.post('removeNotes'),
  uploadDocument: uploadRequest.post('uploadDocument'),
  uploadSupplierDocument: uploadRequest.post('supplier_view/uploadDocuments'),
  rejectDocument: request.post('rejectDocument'),
  duplicateAsMultiGrv: request.post('priority/duplicate-as-multi-grv'),
  getDashboardStatus: rcInstance.get('getDashboardStats'),
  getStatementTxnSearch: request.post('statementTxnSearch'),
  getJournalEntry: rcInstance.get('getJournalEntry'),

  // TODO: partially migrated. The typed equivalent is journalApi.getCellOptions
  // (@/domain/journal/api.ts), which validates the response. Still used by the JE
  // list saga, ListCell/PaginationAutocomplete and the approval dialog — drop
  // this once those three move over.
  getJEList: rcInstance.get('getJournalEntryList'),
  updateJournalEntry: rcInstance.post('updateJournalEntry'),
  updateJournalEntryFromInsights: rcInstance.post('updateJournalEntryFromInsights'),
  getSettings: rcInstance.get('getCompanyPreferences'),
  getSlackConnection: rcInstance.get('slack/connection/status'),
  disconnectSlack: rcInstance.post('slack/connection/revoke'),
  connectSlack: rcInstance.get('slack/install'),
  getSSOParams: request.get('getSSOParams'),
  disconnectErp: request.get('disconnectErp'),
  updateFavoriteCompanies: request.post('updateFavoriteCompanies'),
  acceptDocument: rcInstance.get('acceptDocument'),
  acceptDocumentAsBackgroundTask: rcInstance.post(makeUrl`documents/${'documentID'}/accept`),
  syncDocumentAsBackgroundTask: rcInstance.post(makeUrl`documents/${'documentID'}/sync`),
  checkActivate: publickRequest.post('activateUser'),
  updatePrefs: request.post('updatePrefs'),
  splitDocument: request.post('splitDocument'),
  unlinkDocuments: request.post('unlinkDocuments'),
  getCompanyChatContext: rcInstance.get('getChatPeers'),
  getChatUnreadCounts: rcInstance.get('sendbird/unread'),
  getUserProfile: rcInstance.get('getUserProfile'),
  getMRUTags: request.get('getMRUTags'),
  signDocument: request.post('signDocument'),
  signDocuments: rcInstance.post('signDocuments'),
  bulkAcceptDocuments: rcInstance.post('bulkAcceptDocument'),
  bulkApprove: rcInstance.post('approvals/bulkSignDocuments'),
  bulkSetApprovals: rcInstance.post('bulkSetApprovalFlowNew'),
  moveDocumentToCompany: rcInstance.post('moveDocumentToCompany'),
  moveDocumentsToCompany: rcInstance.post('moveDocumentsToCompany'),
  mergeDocuments: rcInstance.post('mergeDocuments'),
  mergeDocumentsForPrinting: rcInstance.post('getMergedDocuments'),
  getOrganization: rcInstance.get(makeUrl`organizations/${'id'}`),
  updateOrganization: rcInstance.post(makeUrl`organizations/${'id'}`),
  updateOrganizationLogo: rcInstance.post(makeUrl`organizations/${'id'}/uploadLogo`),
  createOrganization: rcInstance.post('organizations'),
  getPasswordExpiration: rcInstance.get(makeUrl`preferences/org/${'id'}/password_expiration`),
  updatePasswordExpiration: rcInstance.post(makeUrl`preferences/org/${'id'}/password_expiration`),
  createCompany: rcInstance.post('company'),
  getAllCompanies: rcInstance.get('company'),
  exportData: rcInstance.url('newExportData'),
  getSignings: rcInstance.get(makeUrl`preferences/${'type'}/${'id'}/signing`),
  updateSignings: rcInstance.put(makeUrl`preferences/${'type'}/${'id'}/signing`),
  removeSignings: rcInstance.delete(makeUrl`preferences/${'type'}/${'id'}/signing`),
  setCompanyFeatures: rcInstance.post(makeUrl`preferences/company/${'id'}/feature_set`),
  getCompanyFeatures: rcInstance.get(makeUrl`preferences/company/${'id'}/feature_set`),
  savePriorityCustomerApiConnection: request.post('priority_api/customer_authorization'),
  getCompanyConnections: rcInstance.get(makeUrl`preferences/company/${'id'}/connections`),
  getCompanyFeaturesLayout: rcInstance.get(makeUrl`preferences/company/${'id'}/layout/features`),
  getCompanyErpLayout: rcInstance.get(makeUrl`preferences/company/${'id'}/layout/erp`),
  getCompanyApprovalFeaturesLayout: rcInstance.get(makeUrl`preferences/company/${'id'}/layout/approval-features`),
  setGuidSignings: rcInstance.post(makeUrl`preferences/${'type'}/${'id'}/signing/set_guid`),
  getSigningsCopy: rcInstance.get(makeUrl`preferences/company/${'id'}/signing/copy`),
  deleteAllSignings: rcInstance.delete(makeUrl`preferences/${'type'}/${'id'}/signing/all`),
  getOrgFeatures: rcInstance.get(makeUrl`preferences/org/${'id'}/feature_set`),
  setOrgFeatures: rcInstance.post(makeUrl`preferences/org/${'id'}/feature_set`),
  getOrgBackupConnections: rcInstance.get(makeUrl`preferences/org/${'id'}/cloud_backup_connections`),
  getOrgFeaturesLayout: rcInstance.get(makeUrl`preferences/org/${'id'}/layout/features`),
  getCompanyUserPreferences: rcInstance.get(makeUrl`preferences/company-user`),
  getBackupSSOParams: rcInstance.get(makeUrl`cloudBackup/getSSOParams`),
  disconnectOrgBackup: rcInstance.get(makeUrl`cloudBackup/disconnect`),
  downloadUsersReport: rcInstance.url('organizations/usersReport'),
  getCompany: rcInstance.get(makeUrl`company/${'id'}`),
  updateCompanyLogo: rcInstance.post(makeUrl`company/${'id'}/uploadLogo`),
  updateCompany: rcInstance.post(makeUrl`company/${'id'}`),
  deleteCompany: rcInstance.delete(makeUrl`company/${'id'}`),
  getCurrentERPSettings: rcInstance.get(makeUrl`preferences/company/${'id'}/erp_config`),
  setCurrentERPSettings: rcInstance.post(makeUrl`preferences/company/${'id'}/erp_config`),
  duplicateDocument: rcInstance.post('duplicateDocument'),
  getDocumentUrl: rcInstance.url('getDocument'),
  getDocumentLinkedCount: rcInstance.get('getCountOfDocumentByLinkpanel'),
  getDocumentCsv: rcInstance.url('exportBankStatement'),
  getDocumentThumbnailUrl: rcInstance.url('getDocumentThumbnail'),
  getDocuments: rcInstance.post('getDocuments'),
  removeDocuments: rcInstance.post('removeDocuments'),
  reprocessDocuments: rcInstance.post('documents/reprocess'),
  stopDocumentProcessing: rcInstance.post('setDocumentAsViewed'),
  headerTablesCorrection: rcInstance.post('headerTablesCorrection'),
  getEvents: rcInstance.get('getEvents'),
  getEventFilters: rcInstance.get('getEventFilters'),
  getCountOfNewEvents: rcInstance.get('getCountOfNewEvents'),
  exportEventsHistory: rcInstance.url('exportEventsHistory'),
  getCurrencies: rcInstance.get('getCurrencies'),
  getTimezones: rcInstance.get('getTimezones'),
  activateErp: rcInstance.get('activateErp'),
  importData: rcInstance.post('importData'),
  getOrganizationList: rcInstance.get('organizations'),
  // updateApproval: rcInstance.mock(baseApprovalMock),
  updateApproval: rcInstance.post('approvals'),
  signApproval: rcInstance.post('approvals/document/sign'),
  rejectApproval: rcInstance.post('approvals/document/reject'),
  // getApprovalRejectReasons: rcInstance.mock(approvalRejectReasons),
  getApprovalApproveReasons: request.get('approvals/getApprovalForm'),
  updateApprovalApproveReasons: request.put('approvals/updateApprovalForm'),
  changeBulkApprovalFlowStatus: request.post('bulkSetStatusApprovalFlow'),
  bulkApprovalReplace: request.post('approvals/document/nodes/bulk_replace'),
  getCategories: rcInstance.get('getWorkspaceCategories'),
  moveDocumentToCategory: rcInstance.post('changeCategoryForDocuments'),
  moveAllCategoryDocumentsToOtherCategory: rcInstance.get('moveDocumentsToCategory'),
  purgeDocuments: rcInstance.post('documents/purge'),
  purgeAllDocuments: rcInstance.post('documents/purge/all'),
  restoreDocuments: rcInstance.post('documents/restore'),
  setDocumentAsCover: rcInstance.post('setDocumentAsCover'),
  gridDocumentsExport: rcInstance.post('grid/documents/export'),
  getWorkspaceGridRows: rcInstance.post('grid/documents/search'),
  getWorkspaceGridHeaders: rcInstance.get('grid/documents/headers'),
  getInsightsGridHeaders: rcInstance.get('grid/insights/headers'),
  getInsightsGridRows: rcInstance.post('grid/insights/search'),
  getDocumentPaymentsGridHeaders: rcInstance.get('grid/payments/headers'),
  getDocumentPaymentsGridRows: rcInstance.post('grid/payments/search'),
  getInsightsGridFilterListByField: rcInstance.post('grid/insights/autocomplete'),
  getDocumentsGridFilterListByField: rcInstance.post('grid/documents/autocomplete'),
  getPresets: rcInstance.get('configs'),
  createPreset: rcInstance.post('configs'),
  updatePreset: rcInstance.put('configs'),
  deletePreset: rcInstance.delete('configs'),
  getPreset: rcInstance.get(makeUrl`configs/${'id'}`),
  getDashboardVendorExpensesHeaders: rcInstance.get('ag-grid/expenses/tree/headers'),
  getDashboardVendorExpensesRows: rcInstance.post('ag-grid/expenses/tree/search'),
  getDashboardVendorExpensesFilterOptions: rcInstance.post('ag-grid/expenses/autocomplete'),
  getVendorBasedApprovalsRows: rcInstance.post('grid/vendor-preferences/approval-flow/search'),
  getVendorBasedApprovalsHeaders: rcInstance.get('grid/vendor-preferences/approval-flow/headers'),
  getVendorBasedApprovalsFilterListByField: rcInstance.post('grid/vendor-preferences/approval-flow/autocomplete'),
  getPolicyManagementGridRows: rcInstance.post('grid/vendor-preferences/policy/search'),
  getPolicyManagementGridHeaders: rcInstance.get('grid/vendor-preferences/policy/headers'),
  updatePolicyManagementGridData: rcInstance.post('vendor-preferences/policy'),
  updateVendorBasedApprovals: rcInstance.post('vendor-preferences/approval-flow'),
  resetVendorBasedApprovalsDefaults: rcInstance.delete('vendor-preferences/approval-flow/default-settings'),
  getVendorBasedApprovalsDefaults: rcInstance.get('vendor-preferences/approval-flow/default-settings'),
  updateVendorBasedApprovalsDefaults: rcInstance.post('vendor-preferences/approval-flow/default-settings'),
  getVendorBasedApprovalsAdditionalEditors: rcInstance.get('vendor-preferences/approval-flow/users'),
  addApproval: rcInstance.post('approvals/document/nodes/add'),
  removeApproval: rcInstance.post('approvals/document/nodes/delete'),
  addAdvancedApprovalGroup: rcInstance.post(makeUrl`approvals/document/advanced-group/add`),
  removeAdvancedApprovalGroup: rcInstance.post(makeUrl`approvals/document/advanced-group/delete`),
  changeOrderApproval: rcInstance.post('approvals/document/nodes/rearrange'),
  setApprovalFlowStatus: rcInstance.post('approvals/document/flow/set_status'),
  getEmailData: rcInstance.get('getEmailData'),
  getDocumentHotkeys: rcInstance.get('hotkeys'),
  updateUserFeatures: rcInstance.post('preferences/user/feature_set'),
  getDocumentsDownloadURL: rcInstance.post('getDocumentsDownloadURL'),
  getSuppliersDefaults: rcInstance.get('supplierFormGrid'),
  updateSuppliersDefaults: rcInstance.post('supplierFormGrid'),
  importCorruptedVendorPreferences: rcInstance.url('vendorPreferencesImportErrorsReport'),
  importVendorPreferences: rcInstance.post('vendorPreferencesFile'),
  exportVendorPreferences: rcInstance.url('vendorPreferencesFile'),
  exportVendorPreferencesNew: rcInstance.url('vendor-preferences/export'),
  importCorruptedVendorPreferencesNew: rcInstance.url('vendor-preferences/errors-report'),
  importVendorPreferencesBackground: rcInstance.post('vendor-preferences/background-import'),
  ignoreDocumentWarnings: rcInstance.post('ignoreWarningDocument'),
  getAliases: rcInstance.get('supplier_view/aliases'),
  updateAliases: rcInstance.post('supplier_view/aliases'),
  // ws
  getWSConnectionUrl: rcInstance.post('notifications/connect'),
  // approvals
  getApprovalGroups: rcInstance.get('approvals/basic-groups'),
  createApprovalsGroup: rcInstance.post('approvals/basic-groups'),
  updateApprovalGroup: rcInstance.post(makeUrl`approvals/basic-groups/${'groupId'}`),
  deleteApprovalsGroup: rcInstance.delete(makeUrl`approvals/basic-groups/${'groupId'}`),
  getApprovers: rcInstance.get('approvals/approvers'),
  getAllApprovalUsers: rcInstance.get('approvals/users'),
  createApprover: rcInstance.post('approvals/approvers'),
  deleteApprover: rcInstance.delete(makeUrl`approvals/approvers/${'approverId'}`),
  getAdvancedApprovalsGroups: rcInstance.get('approvals/advanced-groups'),
  createAdvancedApprovalsGroup: rcInstance.post('approvals/advanced-groups'),
  updateAdvancedApprovalGroup: rcInstance.put(makeUrl`approvals/advanced-groups/${'id'}`),
  deleteAdvancedApprovalsGroup: rcInstance.delete(makeUrl`approvals/advanced-groups/${'id'}`),
  approvalsNodesReplace: rcInstance.post(makeUrl`approvals/document/nodes/replace-all`),
  // textract
  getExtractedTableData: rcInstance.get(makeUrl`tables_data/${'documentID'}`),
  getExtractedTableFieldsMapping: rcInstance.get(makeUrl`tables_data/fields/${'documentID'}`),
  learnExtractedTableFields: rcInstance.post(makeUrl`tables_data/learn_items_headers/${'documentID'}`),
  extractLines: rcInstance.post(makeUrl`tables_data/lines_extraction/${'documentID'}`),
  // organization api keys
  // get user IP address
  notifyOfAppActivity: rcInstance.post('userActivity'),
  createSupportTicket: rcInstance.post('createTicket'),
  pollingDeferredTask: rcInstance.get(makeUrl`tasks/${'taskId'}`),

  getMyPermissionsForPayModule: rcPaymentInstance.get('me/permissions'),
  getPaymentProviders: rcPaymentInstance.get('payment-accounts'),
  connectPaymentProvider: rcPaymentInstance.post('payment-accounts'),
  getPaymentProvider: rcPaymentInstance.get(makeUrl`payment-accounts/${'id'}`),
  requestAccessToProvider: rcPaymentInstance.post(makeUrl`payment-accounts/${'id'}/request-access`),
  disconnectPaymentProvider: rcPaymentInstance.delete(makeUrl`payment-accounts/${'id'}`),
  getProviderPayingAccounts: rcPaymentInstance.get(makeUrl`payment-accounts/${'id'}/funds/paying-accounts`),
  createProviderPayingAccounts: rcPaymentInstance.post(makeUrl`payment-accounts/${'id'}/funds/paying-accounts`),
  updateProviderPayingAccounts: rcPaymentInstance.patch(
    makeUrl`payment-accounts/${'id'}/funds/paying-accounts/${'payingAccountId'}`,
  ),
  disconnectProviderPayingAccounts: rcPaymentInstance.delete(
    makeUrl`payment-accounts/${'id'}/funds/paying-accounts/${'payingAccountId'}`,
  ),
  getProviderUsers: rcPaymentInstance.get(makeUrl`payment-accounts/${'id'}/users`),
  addProviderUsers: rcPaymentInstance.post(makeUrl`payment-accounts/${'id'}/users/groups`),
  deleteProviderUsers: rcPaymentInstance.delete(makeUrl`payment-accounts/${'id'}/users/groups`),
  getProviderApprovers: rcPaymentInstance.get(makeUrl`payment-accounts/${'id'}/approvers`),
  getProviderApprovalTemplate: rcPaymentInstance.get(makeUrl`payment-accounts/${'id'}/approval-template`),
  createProviderApprovalTemplate: rcPaymentInstance.post(makeUrl`payment-accounts/${'id'}/approval-template`),
  updateProviderApprovalTemplate: rcPaymentInstance.put(makeUrl`payment-accounts/${'id'}/approval-template`),
  deleteProviderApprovalTemplate: rcPaymentInstance.delete(makeUrl`payment-accounts/${'id'}/approval-template`),
  getProviderSettings: rcPaymentInstance.get(makeUrl`payment-accounts/${'id'}/settings`),
  updateProviderSettings: rcPaymentInstance.put(makeUrl`payment-accounts/${'id'}/settings`),

  getPayCategories: rcPaymentInstance.get(makeUrl`payment-accounts/${'id'}/workspace-categories`),
  getPaymentsGridHeaders: rcPaymentInstance.get('grid/payments/headers'),
  getPaymentsGridRows: rcPaymentInstance.post('grid/payments/search'),
  getPaymentsGridTotalByCategory: rcPaymentInstance.get('grid/payments/total'),
  getPaymentsGridTotalByState: rcPaymentInstance.get('grid/payments/total-by-state'),
  getPaymentsGridFilterListByField: rcPaymentInstance.post('grid/payments/autocomplete'),
  getPaymentThumbnailUrl: rcPaymentInstance.url('grid/payments/thumbnail'),
  bulkMergePayments: rcPaymentInstance.post('payments/merge'),
  bulkSendToApprovalPayments: rcPaymentInstance.post('payments/send-to-approval'),
  bulkSaveForLaterPayments: rcPaymentInstance.post('payments/save-for-later'),
  bulkArchivePayments: rcPaymentInstance.post('payments/archive'),
  bulkApprovePayments: rcPaymentInstance.post('payments/approve'),
  bulkResetApprovalFlowPayments: rcPaymentInstance.post('payments/reset-approvals'),
  bulkPayPayments: rcPaymentInstance.post('payments/pay'),
  bulkDeletePayments: rcPaymentInstance.post('payments/delete'),

  getPaymentUrl: rcPaymentInstance.url('payments/preview'),
  getPayment: rcPaymentInstance.get(makeUrl`payments/${'id'}`),
  updatePayment: rcPaymentInstance.patch(makeUrl`payments/${'id'}`),
  deletePayment: rcPaymentInstance.delete(makeUrl`payments/${'id'}`),

  startPaymentApprovalFlow: rcPaymentInstance.post(makeUrl`payments/${'id'}/approvals/start`),
  resetPaymentProcess: rcPaymentInstance.post(makeUrl`payments/${'id'}/approvals/reset`),
  approvePayment: rcPaymentInstance.post(makeUrl`payments/${'id'}/approvals/approve`),
  rejectPayment: rcPaymentInstance.post(makeUrl`payments/${'id'}/approvals/reject`),
  payPayment: rcPaymentInstance.post(makeUrl`payments/${'id'}/pay`),
  voidPayment: rcPaymentInstance.post(makeUrl`payments/${'id'}/void`),
  recreatePayment: rcPaymentInstance.post(makeUrl`payments/${'id'}/recreate`),
  unmergePayment: rcPaymentInstance.post(makeUrl`payments/${'id'}/unmerge`),
  updatePaymentTag: rcPaymentInstance.post(makeUrl`payments/${'id'}/tagify`),
  getPreviousPaymentsGridHeaders: rcPaymentInstance.get('grid/previous-payments/headers'),
  getPreviousPaymentsGridRows: rcPaymentInstance.post('grid/previous-payments/search'),
  getPreviousPaymentsGridTotalByCategory: rcPaymentInstance.get('grid/previous-payments/total'),
  getPreviousPaymentsGridFilterListByField: rcPaymentInstance.post('grid/previous-payments/autocomplete'),
};
